import { create } from 'zustand'
import {
    hrSuggestionService,
    type PotentialHRPartner,
    type GroupedHRResponse,
    type PotentialHRPartnersParams,
} from '../services/hrSuggestionService'

interface HRSuggestionState {
    potentialHRPartners: PotentialHRPartner[]
    groupedHRPartners: GroupedHRResponse | null
    suggestedHRPartner: PotentialHRPartner | null
    isLoading: boolean
    error: string | null

    fetchPotentialHRPartners: (params: PotentialHRPartnersParams) => Promise<void>
    fetchSuggestedHRPartner: (scopeInstanceId: string, employeeId?: string) => Promise<void>
    reset: () => void
}

export const useHRSuggestionStore = create<HRSuggestionState>((set) => ({
    potentialHRPartners: [],
    groupedHRPartners: null,
    suggestedHRPartner: null,
    isLoading: false,
    error: null,

    async fetchPotentialHRPartners(params) {
        set({ isLoading: true, error: null })

        try {
            const data = await hrSuggestionService.getPotentialHRPartners(params)

            // Check if response is grouped or flat
            if (params.grouped && 'groups' in data) {
                set({
                    groupedHRPartners: data as GroupedHRResponse,
                    suggestedHRPartner: (data as GroupedHRResponse).suggested,
                    potentialHRPartners: [],
                    isLoading: false,
                })
            } else {
                set({
                    potentialHRPartners: data as PotentialHRPartner[],
                    groupedHRPartners: null,
                    suggestedHRPartner: (data as PotentialHRPartner[])[0] || null,
                    isLoading: false,
                })
            }
        } catch (error) {
            console.error('Failed to fetch potential HR partners', error)
            set({
                error: error instanceof Error ? error.message : 'Unable to load potential HR partners',
                isLoading: false,
            })
            throw error
        }
    },

    async fetchSuggestedHRPartner(scopeInstanceId, employeeId) {
        set({ isLoading: true, error: null })

        try {
            const data = await hrSuggestionService.getSuggestedHRPartner(scopeInstanceId, employeeId)
            set({
                suggestedHRPartner: data.suggested,
                isLoading: false,
            })
        } catch (error) {
            console.error('Failed to fetch suggested HR partner', error)
            set({
                error: error instanceof Error ? error.message : 'Unable to load suggested HR partner',
                isLoading: false,
            })
            throw error
        }
    },

    reset() {
        set({
            potentialHRPartners: [],
            groupedHRPartners: null,
            suggestedHRPartner: null,
            isLoading: false,
            error: null,
        })
    },
}))
